// Code taken from here: https://webdesign.tutsplus.com/tutorials/javascript-debounce-and-throttle--cms-36783

let debounceTimer: ReturnType<typeof setTimeout>;
let throttlePause: boolean;

/* debounce VS throttle usecases
 * Debounce is good for when you want to hold back a repeating event from firing,
 * until a timeout occurs.
 * Common usecase: input event on an input field. While the user is typing in
 * the input element, we don't want to submit the value to the API. Once a timeout
 * has been reached, because the user stopped typing, the entire value that has
 * been typed, will be sent as a single API request. Once said timeout has reached,
 * it will be cleared, and upon the next key strokes, a new timeout will be created.
 *
 * Throttle is good for when we want to minimize the number of events triggered
 * per a time-unit, but guarantee an event to fire every given timeout-unit.
 * In other words: Update the UI in the current state of a repeating event such as
 * scroll.
 * Common usecase: Change the page's URL upon scrolling down, navigating between
 * sections, where each section has an anchor link that will be represnted in the
 * URL. Scrolling down will not trigger our function too often. The function will
 * be called only once every timeout-unit, sampling the current state of the scroll
 * position, and update the URL accordingly.
 */
const debounce = (callback: Function, time: number) => {
  window.clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(callback, time);
};

const throttle = (callback: Function, time: number) => {
  //don't run the function if throttlePause is true
  if (throttlePause) return;

  //set throttlePause to true after the if condition. This allows the function to be run once
  throttlePause = true;

  //setTimeout runs the callback within the specified time
  setTimeout(() => {
    //throttlePause is set to false once the function has been called, allowing the throttle function to loop
    throttlePause = false;
  }, time);
  callback();
};

const BFF_BASE_PATH = "/webapi";

// code taken from: https://stackoverflow.com/questions/27936772/how-to-deep-merge-instead-of-shallow-merge
/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
const isObject = (item: any) => {
  return item && typeof item === "object" && !Array.isArray(item);
};

/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 */
const mergeDeep = (target: any, ...sources: any): any => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep(target, ...sources);
};

const inputElements = ["input", "textarea"];

export { throttle, debounce, mergeDeep, BFF_BASE_PATH, inputElements };
